import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import SystemSettingsForm from '../../components/SystemSettings/SystemSettingsForm';
import ErrorAlert from '../../components/widgets/ErrorAlert';
import HeadingWithIcon from '../../components/widgets/HeadingWithIcon';
import LoadingSpinner from '../../components/widgets/LoadingSpinner';
import SuccessAlert from '../../components/widgets/SuccessAlert';
import axiosInstance from '../../network/ApiCall';
import {
  ADMINISTRATION_LOGO_URL,
  ADMINISTRATION_NAME,
  SENDER_ID,
  SYSTEM_DEFAULT_COUNTRY,
  SYSTEM_UNION_CODE
} from '../../config';
import SystemSettingsTable from '../../components/SystemSettings/SystemSettingsTable';

const SystemSettings = (props) => {

  const SYSTEM_SETTINGS_ENDPOINT = "/settings";

  const {t} = useTranslation();

  const authState = useSelector((state) => state.auth);
  const params = useParams();

  const [formState, setFormState] = useState({success: false, error: null, submissionDate: null});
  const [defaultData, setDefaultData] = useState();
  const [loading, setLoading] = useState(true);

  const userCountryCode = authState?.user?.countryCode;
  const unionModeEnabled = Boolean(SYSTEM_UNION_CODE);
  const isUnionAdmin = authState?.user?.roles?.includes('UNION_ADMIN');
  const routeCountryCode = params?.countryCode;

  const normalizedCountryCode = useMemo(() => {
    if (routeCountryCode) {
      return routeCountryCode.toUpperCase();
    }
    if (!unionModeEnabled || !isUnionAdmin) {
      return (userCountryCode || SYSTEM_DEFAULT_COUNTRY || '').toUpperCase();
    }
    return '';
  }, [isUnionAdmin, routeCountryCode, unionModeEnabled, userCountryCode]);

  const shouldShowTable = unionModeEnabled && isUnionAdmin && !routeCountryCode;

  const toFormValues = useCallback((dto = {}) => ({
    countryCode: dto.countryCode ?? normalizedCountryCode ?? SYSTEM_DEFAULT_COUNTRY ?? '',
    senderId: dto.senderId ?? SENDER_ID ?? '',
    administrationName: dto.administrationName ?? ADMINISTRATION_NAME ?? '',
    administrationLogoUrl: dto.administrationLogoUrl ?? ADMINISTRATION_LOGO_URL ?? '',
    contactInstructions: dto.contactInstructions ?? '',
    defaultCanPerformOperations: dto.defaultCanPerformOperations ?? false,
    defaultCanChangeOwnOffice: dto.defaultCanChangeOwnOffice ?? false,
    defaultAdminCanManageSysOptionsAndUsers: dto.defaultAdminCanManageSysOptionsAndUsers ?? false,
    defaultAdminCanManageSettings: dto.defaultAdminCanManageSettings ?? false,
  }), [normalizedCountryCode]);

  const resolveSettingsDto = useCallback((payload) => {
    if (Array.isArray(payload)) {
      if (!payload.length) {
        return {};
      }
      const countryMatch = payload.find((item) =>
        (item?.countryCode || '').toUpperCase() === normalizedCountryCode
      );
      return countryMatch || payload[0];
    }
    return payload || {};
  }, [normalizedCountryCode]);

  useEffect(() => {

    let ignore = false;

    const loadSettings = async () => {
      if (shouldShowTable) {
        setDefaultData(undefined);
        setFormState({success: false, error: null, submissionDate: null});
        setLoading(false);
        return;
      }

      if (!normalizedCountryCode) {
        if (authState.loading || authState.loadingUser) {
          return;
        }
        setDefaultData(undefined);
        setFormState({success: false, error: t('idpSettings:msgMissingCountry'), submissionDate: new Date()});
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let response;
        if (unionModeEnabled) {
          try {
            response = await axiosInstance.get(`${SYSTEM_SETTINGS_ENDPOINT}/${normalizedCountryCode}`);
          } catch (error) {
            if (error?.response?.status === 404) {
              response = await axiosInstance.get(SYSTEM_SETTINGS_ENDPOINT);
            } else {
              throw error;
            }
          }
        } else {
          response = await axiosInstance.get(SYSTEM_SETTINGS_ENDPOINT);
        }
        if (ignore) {
          return;
        }
        setDefaultData(toFormValues(resolveSettingsDto(response?.data)));
        setFormState({success: false, error: null, submissionDate: null});
      } catch (error) {
        if (ignore) {
          return;
        }
        console.error('ERROR LOADING SYSTEM SETTINGS: ', error);
        setDefaultData(undefined);
        setFormState({success: false, error: t('common:msgUnknownApiError'), submissionDate: new Date()});
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      ignore = true;
    };
  }, [authState.loading, authState.loadingUser, normalizedCountryCode, resolveSettingsDto, shouldShowTable, t, toFormValues, unionModeEnabled]);

  const onSubmit = (data) => {
    const endpoint = normalizedCountryCode ? `${SYSTEM_SETTINGS_ENDPOINT}/${normalizedCountryCode}` : SYSTEM_SETTINGS_ENDPOINT;

    axiosInstance.post(endpoint, data).then(response => {
      const responseData = response?.data
        ? toFormValues(resolveSettingsDto(response.data))
        : toFormValues(data);
      setDefaultData(responseData);
      setFormState({success: true, error: null, submissionDate: new Date()});
    })
      .catch(error => {
        console.error('ERROR SAVING SYSTEM SETTINGS: ', error);
        setFormState({success: false, error: t("common:msgUnknownApiError"), submissionDate: new Date()});
      });
  };

  const onError = (errors, e) => console.warn('SYSTEM SETTINGS FORM ERROR', errors, e);

  return (
    <div className="content p-4">

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">{t('dashboard:hdgHeading')}</Link></li>
          <li className="breadcrumb-item active" aria-current="page">{t('systemSettings:hdgHeading')}</li>
        </ol>
      </nav>
      <div className="m-2 m-sm-5">


        <div className="card my-5">
          <div className="card-header ">
            <div className="row">
              <div className="col">
                <HeadingWithIcon icon="fa-cogs">{t('systemSettings:hdgHeading')}</HeadingWithIcon>
              </div>
            </div>
          </div>
          <div className="card-body ">
            {shouldShowTable ? <SystemSettingsTable/> : (
              <div className="row ">
                <div className="col-md-8 offset-md-2 mb-5">
                  {formState.success &&
                    <SuccessAlert message={t('common:msgChangesSaved')} lastStateUpdate={formState.submissionDate}/>}
                  <ErrorAlert error={formState.error} lastStateUpdate={formState.submissionDate}/>

                  {loading ? <LoadingSpinner/> : <>
                    {defaultData &&
                      <SystemSettingsForm defaultValues={defaultData} onSubmit={onSubmit} onError={onError}/>}
                  </>}

                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
